/* tslint:disable:max-file-line-count */

import { action, computed, intercept, observable } from 'mobx';
import { createViewModel } from 'mobx-utils';

import { getErrorString } from 'bernie-logger';
import { ILocalization, Localization } from 'bernie-l10n';
import { Logger, NOOP_LOGGER } from 'bernie-logger';
import { SerializedData, Store } from 'bernie-core';

import { STORE_EVENTS } from './events/store-events';

import { InfositeQueryAdapter } from './adapters/infosite-query-adapter';
import { SearchQueryAdapter } from './adapters/search-query-adapter';

/**
 * @deprecated Use SearchCriteriaModel
 */
export class SearchCriteriaStore extends Store {
  public static MIDNIGHT_BOOKING_OFFSET_IN_HOURS = 4; // 4AM
  public static NUM_OF_DAYS_BOOKABLE_IN_ADVANCE = 500;

  public static getDefaults() {
    return {
      adults: '2',
      children: '',
      destination: '',
      endDate: null,
      group: '',
      latLong: '',
      regionId: '',
      selected: '',
      startDate: null,
      userIntent: ''
    };
  }

  public static getFilterDefaults() {
    return {
      accessibility: [],
      amenities: [],
      guestRating: '',
      hotelName: '',
      lodging: [],
      poi: '',
      price: [],
      sort: '',
      star: [],
      travelerType: [],
      paymentType: [],
      bedroomFilter: [],
      deals: [],
      propertyStyle: [],
      rewards: [],
      commissionTier: [],
      agencyBusinessModel: [],
      mealPlan: []
    };
  }

  static get getFilterKeys() {
    return [
      {
        key: 'sort',
        type: 'string'
      },
      {
        key: 'hotelName',
        type: 'string'
      },
      {
        key: 'price',
        type: 'array'
      },
      {
        key: 'star',
        type: 'array'
      },
      {
        key: 'lodging',
        type: 'array'
      },
      {
        key: 'amenities',
        type: 'array'
      },
      {
        key: 'travelerType',
        type: 'array'
      },
      {
        key: 'poi',
        type: 'string'
      },
      {
        key: 'guestRating',
        type: 'string'
      },
      {
        key: 'accessibility',
        type: 'array'
      },
      {
        key: 'paymentType',
        type: 'array'
      },
      {
        key: 'bedroomFilter',
        type: 'array'
      },
      {
        key: 'deals',
        type: 'array'
      },
      {
        key: 'propertyStyle',
        type: 'array'
      },
      {
        key: 'rewards',
        type: 'array'
      },
      {
        key: 'commissionTier',
        type: 'array'
      },
      {
        key: 'agencyBusinessModel',
        type: 'array'
      },
      {
        key: 'mealPlan',
        type: 'array'
      }
    ];
  }

  public localDateFormat: string;

  constructor(state: SerializedData = {}, l10n: ILocalization, logger: Logger = NOOP_LOGGER) {
    super(state, logger);

    this.l10n = l10n || new Localization('en_US');
    this.localDateFormat = state.localDateFormat || 'yyyy-MM-dd';

    this.dateInterceptor('startDate');
    this.dateInterceptor('endDate');

    Object.assign(this, SearchCriteriaStore.getDefaults(), SearchCriteriaStore.getFilterDefaults(), state);
  }

  public hydrate(data: SerializedData): void {
    Object.assign(this, data);
  }

  // setup mobx interceptor to wrap dates in a method to format data passed to setter
  public dateInterceptor(prop): void {
    if (typeof prop === 'string') {
      intercept(this, prop as any, change => {
        if (change.newValue) {
          const newValue = this.sanitizeDate(change.newValue);

          if (newValue) {
            change.newValue = newValue;

            return change;
          }
        }
        // accept null to clear out dates
        else if (change.newValue === null) {
          return change;
        }
      });
    }
  }

  // keep dates in the data format that we want, in this case ISO, and the UI can localize based on this date
  public sanitizeDate(date) {
    if (date) {
      try {
        let dateObj: Date = this.l10n.parseLocalDateString(date, { raw: this.localDateFormat }) as Date;

        if (!dateObj) {
          dateObj = this.l10n.parseLocalDateString(date, { raw: 'yyyy-MM-dd' }) as Date;
        }

        const cleanDate = this.l10n.formatDate(dateObj, { raw: 'yyyy-MM-dd' });

        if (cleanDate) {
          return cleanDate;
        }

        throw new Error(`Invalid Date: cannot parse ${date} into proper date`);
      } catch (error) {
        if (this.logger) {
          this.logger.logEvent(STORE_EVENTS.MOBX_INTERCEPTOR_ERROR, getErrorString(error));
        }

        return '';
      }
    }
  }

  // return a whitelisted or blacklisted version of the store
  public toJSON(toBeRemoved = []) {
    return super.toJSON(['batchRequest', 'changedFilters', 'l10n'].concat(toBeRemoved));
  }

  @observable
  public l10n: ILocalization;

  // Search Form
  @observable
  public startDate = null;
  @observable
  public endDate = null;
  @observable
  public destination = '';
  @observable
  public regionId = '';
  @observable
  public adults = '2';
  @observable
  public children = '';
  @observable
  public selected = '';
  @observable
  public experience = '';

  // Filter Form
  @observable
  public sort = '';
  @observable
  public hotelName = '';
  @observable
  public price = [];
  @observable
  public star = [];
  @observable
  public lodging = [];
  @observable
  public amenities = [];
  @observable
  public travelerType = [];
  @observable
  public guestRating = '';
  @observable
  public changedFilters: any = {};
  @observable
  public accessibility = [];
  @observable
  public paymentType = [];
  @observable
  public bedroomFilter = [];
  @observable
  public deals = [];
  @observable
  public propertyStyle = [];
  @observable
  public rewards = [];
  @observable
  public commissionTier = [];
  @observable
  public agencyBusinessModel = [];
  @observable
  public mealPlan = [];
  @observable
  public poi = '';

  // Code refactoring need to be done in order to implement batch request functionality on UI.
  // Adding a field here in order to make minimal changes and to make sure that the existing functionality dont break
  // when the backend code gets merged.
  @observable
  public batchRequest: any = {};

  // Optional fields
  @observable
  public latLong = '';

  private searchCriteria: any;

  @computed
  get guestCount(): number {
    return this.adultsCount + this.childrenCount;
  }

  @computed
  get adultsCount(): number {
    let adults;
    if (typeof this.adults === 'number') {
      adults = this.adults;
    } else if (typeof this.adults === 'string' && this.adults !== '') {
      adults = parseInt(
        this.adults.split(',').reduce((acc, cur) => String(parseInt(acc, 10) + parseInt(cur, 10))),
        10
      );
    } else {
      adults = 0;
    }

    return adults;
  }

  @computed
  get childrenCount(): number {
    let children;
    if (typeof this.children === 'number') {
      children = this.children;
    } else if (typeof this.children === 'string' && this.children !== '') {
      children = this.children.split(',').length;
    } else {
      children = 0;
    }

    return children;
  }

  @computed
  get roomCount(): number {
    return this.adults.split(',').length;
  }

  @action
  public clearFilters(): void {
    Object.assign(this, SearchCriteriaStore.getFilterDefaults());
    this.changedFilters = {};
  }

  public didFiltersChange(viewModel: any): { didChange: boolean; currentFilterCount: number } {
    if (!viewModel) {
      throw new Error('Must pass viewModel');
    }

    const strings = SearchCriteriaStore.getFilterKeys.filter(val => val.type === 'string');
    const stringsEqual: boolean = strings.every(val => viewModel[val.key] === this[val.key]);

    const arrays = SearchCriteriaStore.getFilterKeys.filter(val => val.type === 'array');
    let arraysEqual: boolean = true;

    let currentFilterCount = 0;

    if (viewModel.hotelName) {
      currentFilterCount = 1;
    }

    if (viewModel.sort) {
      currentFilterCount += 1;
    }

    arrays.forEach(val => {
      const current = viewModel[val.key].slice();
      const original = this[val.key].slice();

      if (arraysEqual === true) {
        arraysEqual = current.every((currVal, index) => original[index] === currVal) && current.length === original.length;
      }

      currentFilterCount += current.length > 0 ? 1 : 0;
    });

    return {
      didChange: !stringsEqual || !arraysEqual,
      currentFilterCount
    };
  }

  @action
  public setValues(query: any = {}): void {
    if (!query.adults) {
      query.adults = '2';
    }

    if (!query.children || query.children === '0' || query.children === 0) {
      query.children = '';
    }

    // iterate through the filters setting them to a parsed value or their default
    SearchCriteriaStore.getFilterKeys.forEach(({ key, type }) => {
      if (key in query) {
        // if it's a string but it's supposed to be an array
        if (type === 'array' && typeof query[key] === 'string') {
          // parse based on comma separator
          query[key] = query[key].split(',').filter(val => val !== '');
        }
        // if it's a array but it's supposed to be an string
        if (type === 'string' && Array.isArray(query[key])) {
          query[key] = query[key].join();
        }
      } else {
        query[key] = SearchCriteriaStore.getFilterDefaults()[key];
      }
    });

    Object.assign(this, query);
  }

  @action
  public setInfositeValues(query: any): void {
    query = InfositeQueryAdapter.parseIn(query);
    let { adults, children } = SearchQueryAdapter.parseOut({ ...query });

    adults = adults || '2';
    children = children || '';

    this.setValues({ ...query, adults, children });
  }

  public createViewModel(): any {
    const viewModel = createViewModel(this);
    viewModel.clearFilters = this.clearFilters; // mixin clearFilters action
    viewModel.didFiltersChange = this.didFiltersChange.bind(this, viewModel);
    viewModel.updateChangedFilters = this.updateChangedFilters.bind(this);

    return viewModel;
  }

  @action
  public cleanViewModel(viewModel) {
    if (viewModel && viewModel.model === this && viewModel.isPropertyDirty('destination')) {
      viewModel.clearFilters();
    }
  }

  @action
  public submitViewModel(viewModel) {
    if (viewModel && viewModel.model === this) {
      viewModel.submit();
    }
  }

  @action
  public getOfferData(): any {
    const { startDate, endDate, adults, children } = this;

    return {
      startDate,
      endDate,
      adults,
      children
    };
  }

  @action
  public getETPData(): any {
    const { startDate, endDate, adults } = this;

    return {
      startDate,
      endDate,
      adults
    };
  }

  @action
  public mergeCriteria(criteria): any {
    return Object.assign({}, this.searchCriteria, criteria);
  }

  @action
  public updateChangedFilters(property, value): any {
    if (typeof value === 'object' && value.length <= 0) {
      delete this.changedFilters[property];

      return this.changedFilters;
    }

    return Object.assign(this.changedFilters, { [property]: value });
  }

  /*
  Temporary method to test desktop work.
  TODO: Remove when feature is launched.
 */
  @computed
  get isDesktopExperience(): boolean {
    return this.experience === 'desktop';
  }
}
